import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { createNews, formatApiError } from '../lib/api';
import { useFileUpload } from '../hooks/useFileUpload';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import RichTextEditor from '../components/RichTextEditor';
import NewsPreview from '../components/NewsPreview';
import { Upload, Image, Video, Loader2, ArrowLeft, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const CATS = ["Política","Deportes","Tecnología","Negocios","Entretenimiento","Cultura"];

export default function CreateNewsPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { imageRef, videoRef, imageUrl, videoUrl, uploading, handleImageUpload, handleVideoUpload } = useFileUpload();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [summary, setSummary] = useState('');
  const [category, setCategory] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(true);

  const submit = async (e) => {
    e.preventDefault(); setError('');
    if (!category) { setError('Selecciona categoría'); return; }
    const plain = content.replace(/<[^>]*>/g, '').trim();
    if (plain.length < 20) { setError('El contenido debe tener al menos 20 caracteres'); return; }
    setSubmitting(true);
    try { await createNews({ title, content, summary, category, image_url: imageUrl, video_url: videoUrl }); toast.success(user.role==='admin'?'Publicada':'Enviada para aprobación'); nav('/dashboard'); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  if (!user) { nav('/login'); return null; }

  return (
    <div data-testid="create-news-page" className={`${showPreview ? 'max-w-[1400px]' : 'max-w-3xl'} mx-auto px-4 sm:px-6 py-8 transition-all`}>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-[10px] font-black text-[#0B1F40]/30 hover:text-[#E50B15] tracking-[0.15em] uppercase"><ArrowLeft className="h-3 w-3"/> VOLVER</button>
        <button type="button" data-testid="toggle-preview" onClick={() => setShowPreview(p => !p)}
          className="hidden lg:inline-flex items-center gap-1.5 h-8 px-3 border-2 border-[#0B1F40]/20 hover:border-[#0B1F40] text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/70 transition-colors">
          {showPreview ? <><EyeOff className="h-3 w-3" /> OCULTAR VISTA</> : <><Eye className="h-3 w-3" /> MOSTRAR VISTA</>}
        </button>
      </div>

      <div className={`grid gap-6 ${showPreview ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
        {/* Form */}
        <div className="bg-white border-2 border-[#0B1F40] p-6 sm:p-8 brutal-shadow h-fit">
          <h1 className="text-xl sm:text-2xl text-[#0B1F40] tracking-tighter mb-6" style={{fontFamily:'Archivo Black'}}>CREAR NOTICIA</h1>
          {user.role!=='admin'&&(
            <div className="mb-5 p-3 bg-amber-50 border-l-4 border-amber-500 text-xs font-bold text-amber-700 flex items-center gap-2">
              <AlertCircle className="h-3.5 w-3.5 shrink-0"/> Será revisada por un admin antes de publicarse.
            </div>
          )}
          {error&&<div data-testid="create-news-error" className="mb-4 p-3 bg-red-50 border-l-4 border-[#E50B15] text-[#E50B15] text-xs font-bold">{error}</div>}
          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/50">TÍTULO</Label>
              <Input data-testid="news-title-input" value={title} onChange={e=>setTitle(e.target.value)}
                className="rounded-none border-2 border-[#0B1F40]/20 focus:border-[#E50B15] focus:ring-0 mt-1 h-11 text-lg font-bold" placeholder="Título" required/>
            </div>
            <div>
              <Label className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/50">CATEGORÍA</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="news-category-select" className="rounded-none border-2 border-[#0B1F40]/20 focus:border-[#E50B15] mt-1 h-11"><SelectValue placeholder="Selecciona"/></SelectTrigger>
                <SelectContent className="rounded-none border-2 border-[#0B1F40]">{CATS.map(c=><SelectItem key={c} value={c} data-testid={`category-option-${c.toLowerCase()}`}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/50">RESUMEN</Label>
              <Input data-testid="news-summary-input" value={summary} onChange={e=>setSummary(e.target.value)}
                className="rounded-none border-2 border-[#0B1F40]/20 focus:border-[#E50B15] focus:ring-0 mt-1 h-11" placeholder="Breve resumen"/>
            </div>
            <div>
              <Label className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/50">CONTENIDO</Label>
              <div className="mt-1"><RichTextEditor value={content} onChange={setContent} placeholder="Escribe el contenido de la noticia…" testid="news-content-input" /></div>
            </div>
            <div className="flex flex-wrap gap-3">
              <div>
                <input ref={imageRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleImageUpload} className="hidden"/>
                <button type="button" data-testid="upload-image-btn" onClick={()=>imageRef.current?.click()} disabled={uploading}
                  className="border-2 border-[#0B1F40]/20 text-[#0B1F40] text-[10px] font-black h-9 px-4 flex items-center gap-1.5 hover:border-[#0B1F40] transition-colors">
                  {uploading?<Loader2 className="h-3 w-3 animate-spin"/>:<Image className="h-3 w-3"/>} IMAGEN PRINCIPAL
                </button>
                {imageUrl&&<span className="block text-[10px] text-emerald-600 font-black mt-1">CARGADA</span>}
              </div>
              <div>
                <input ref={videoRef} type="file" accept="video/mp4,video/webm" onChange={handleVideoUpload} className="hidden"/>
                <button type="button" data-testid="upload-video-btn" onClick={()=>videoRef.current?.click()} disabled={uploading}
                  className="border-2 border-[#0B1F40]/20 text-[#0B1F40] text-[10px] font-black h-9 px-4 flex items-center gap-1.5 hover:border-[#0B1F40] transition-colors">
                  {uploading?<Loader2 className="h-3 w-3 animate-spin"/>:<Video className="h-3 w-3"/>} VIDEO
                </button>
                {videoUrl&&<span className="block text-[10px] text-emerald-600 font-black mt-1">CARGADO</span>}
              </div>
            </div>
            <button data-testid="submit-news-btn" type="submit" disabled={submitting}
              className="w-full bg-[#E50B15] text-white font-black text-sm h-11 flex items-center justify-center gap-2 brutal-btn-press transition-all" style={{boxShadow:'4px 4px 0px #0B1F40'}}>
              {submitting?<Loader2 className="h-4 w-4 animate-spin"/>:<Upload className="h-4 w-4"/>}
              {user.role==='admin'?'PUBLICAR':'ENVIAR PARA APROBACIÓN'}
            </button>
          </form>
        </div>

        {/* Live preview */}
        {showPreview && (
          <aside className="hidden lg:block lg:sticky lg:top-24 h-fit">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-[10px] font-black tracking-[0.3em] uppercase text-[#E50B15]">VISTA PREVIA EN VIVO</span>
              <span className="text-[9px] font-bold text-[#0B1F40]/30 uppercase tracking-wider">Actualización automática</span>
            </div>
            <div className="border-2 border-[#0B1F40] overflow-auto max-h-[calc(100vh-180px)]" style={{ boxShadow: '6px 6px 0 #E50B15' }}>
              <NewsPreview title={title} category={category} summary={summary} content={content} imageUrl={imageUrl} videoUrl={videoUrl} author={user.name} />
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
